import { z } from 'zod';
import { ClientLifecycleStageSchema } from './crm.js';
import { AmountSchema } from './money.js';
import { BusinessDateSchema, IdSchema } from './encoding.js';
/**
 * Marketing contracts (ERD §7.6; FRD-MKT-005 to MKT-031, MKT-035, MKT-044, MKT-045). Only the
 * Release 1 scope: the lead register and campaigns (FRD-MKT-001). Campaign responses, consent,
 * marketing activities, attribution processing and campaign spend are later or blocked (CLR-18).
 *
 * The Lead ID and Campaign ID (`code`) are generated by the system from their configured series
 * (FRD-MKT-006, MKT-018): in every response and in no request. Source, industry, priority, status,
 * qualification status, channel and target audience carry the value code of a configurable list
 * (SDD §15.3); the values are business data.
 */
const NameSchema = z.string().trim().min(1).max(200);
const EmailSchema = z.email().max(254);
const PhoneSchema = z.string().trim().min(1).max(50);
const ListCodeValueSchema = z.string().trim().min(1).max(100);
const LongTextSchema = z.string().trim().min(1).max(4000);
const NextActionSchema = z.string().trim().min(1).max(1000);
const InterestSchema = z.string().trim().min(1).max(2000);
/** A campaign budget as entered: a non-negative amount with at most 2 decimals (FRD-MKT-035). */
const BudgetInputSchema = z
    .string()
    .trim()
    .regex(/^\d{1,17}(\.\d{1,2})?$/, 'must be a decimal amount with at most 2 decimals');
// ------------------------------------------------------------------------------------- leads
export const LeadSchema = z.object({
    id: IdSchema,
    /** The generated Lead ID (FRD-MKT-006). */
    code: z.string(),
    companyName: z.string(),
    contactName: z.string().nullable(),
    contactEmail: z.string().nullable(),
    contactPhone: z.string().nullable(),
    sourceCode: z.string().nullable(),
    /** A value of the CRM industry list (`crm.industry`). */
    industryCode: z.string().nullable(),
    interest: z.string().nullable(),
    /** The responsible employee (FRD-MKT-009). */
    assignedEmployeeId: IdSchema,
    priorityCode: z.string().nullable(),
    statusCode: z.string(),
    qualificationStatusCode: z.string().nullable(),
    lastContactDate: z.string().nullable(),
    nextAction: z.string().nullable(),
    nextActionDueDate: z.string().nullable(),
    /** The campaign that generated the lead (FRD-MKT-012), with its ID and name. */
    campaignId: IdSchema.nullable(),
    campaignCode: z.string().nullable(),
    campaignName: z.string().nullable(),
    /**
     * The client the lead converted into or relates to (FRD-MKT-030, MKT-031). Set only by the
     * convert and link actions.
     */
    clientId: IdSchema.nullable(),
    /** The client's ID and name, only for a caller who may view clients; otherwise null. */
    clientLabel: z.string().nullable(),
    /** Set when CRM created the client from this lead; null when linked to an existing client. */
    conversionDate: z.string().nullable(),
    /** When the lead was recorded (UTC). */
    createdAt: z.string(),
    /** For `If-Match` on a change (SDD §7.3). */
    version: z.number().int(),
});
export const CreateLeadRequestSchema = z.strictObject({
    companyName: NameSchema,
    contactName: NameSchema.optional(),
    contactEmail: EmailSchema.optional(),
    contactPhone: PhoneSchema.optional(),
    sourceCode: ListCodeValueSchema.optional(),
    industryCode: ListCodeValueSchema.optional(),
    interest: InterestSchema.optional(),
    assignedEmployeeId: IdSchema,
    priorityCode: ListCodeValueSchema.optional(),
    statusCode: ListCodeValueSchema,
    qualificationStatusCode: ListCodeValueSchema.optional(),
    lastContactDate: BusinessDateSchema.optional(),
    nextAction: NextActionSchema.optional(),
    nextActionDueDate: BusinessDateSchema.optional(),
    campaignId: IdSchema.optional(),
});
/**
 * A partial change: edit, reassignment (`assignedEmployeeId`), status and qualification changes,
 * and recording a contact (`lastContactDate` with the next action). An optional field is cleared
 * with `null`. The Lead ID and the client link never change here.
 */
export const UpdateLeadRequestSchema = z.strictObject({
    companyName: NameSchema.optional(),
    contactName: NameSchema.nullable().optional(),
    contactEmail: EmailSchema.nullable().optional(),
    contactPhone: PhoneSchema.nullable().optional(),
    sourceCode: ListCodeValueSchema.nullable().optional(),
    industryCode: ListCodeValueSchema.nullable().optional(),
    interest: InterestSchema.nullable().optional(),
    assignedEmployeeId: IdSchema.optional(),
    priorityCode: ListCodeValueSchema.nullable().optional(),
    statusCode: ListCodeValueSchema.optional(),
    qualificationStatusCode: ListCodeValueSchema.nullable().optional(),
    lastContactDate: BusinessDateSchema.nullable().optional(),
    nextAction: NextActionSchema.nullable().optional(),
    nextActionDueDate: BusinessDateSchema.nullable().optional(),
    campaignId: IdSchema.nullable().optional(),
});
/**
 * Convert a lead to a client (FRD-MKT-029, FRD-CRM-047): the client fields CRM needs that the lead
 * does not carry. CRM validates them exactly as for any client. The company name defaults to the
 * lead's, the account owner to the lead's assignee and the industry to the lead's.
 * `recordContact` also records the lead's contact person as a contact of the new client.
 */
export const ConvertLeadRequestSchema = z.strictObject({
    companyName: NameSchema.optional(),
    lifecycleStage: ClientLifecycleStageSchema,
    accountOwnerEmployeeId: IdSchema.optional(),
    industryCode: ListCodeValueSchema.nullable().optional(),
    clientTypeCode: ListCodeValueSchema.optional(),
    sourceCode: ListCodeValueSchema.optional(),
    website: z.string().trim().min(1).max(500).optional(),
    email: EmailSchema.optional(),
    phone: PhoneSchema.optional(),
    notes: LongTextSchema.optional(),
    recordContact: z.boolean(),
});
/** Relate a lead to a client that already exists, instead of duplicating it (FRD-MKT-031). */
export const LinkLeadClientRequestSchema = z.strictObject({
    clientId: IdSchema,
});
// --------------------------------------------------------------------------------- campaigns
export const CampaignSchema = z.object({
    id: IdSchema,
    /** The generated Campaign ID (FRD-MKT-018). */
    code: z.string(),
    name: z.string(),
    objective: z.string().nullable(),
    startDate: z.string(),
    endDate: z.string().nullable(),
    /** Planning information only, not an accounting budget (FRD-MKT-035). */
    budget: AmountSchema.nullable(),
    targetAudienceCode: z.string().nullable(),
    channelCode: z.string().nullable(),
    /** The responsible employee (FRD-MKT-021). */
    ownerEmployeeId: IdSchema,
    statusCode: z.string(),
    /** Recorded only; attribution processing is Phase 3 (FRD-MKT-020). */
    attributionModel: z.string().nullable(),
    /** Results recorded against the campaign (FRD-MKT-019). */
    results: z.string().nullable(),
    version: z.number().int(),
});
export const CreateCampaignRequestSchema = z.strictObject({
    name: NameSchema,
    objective: LongTextSchema.optional(),
    startDate: BusinessDateSchema,
    endDate: BusinessDateSchema.optional(),
    budget: BudgetInputSchema.optional(),
    targetAudienceCode: ListCodeValueSchema.optional(),
    channelCode: ListCodeValueSchema.optional(),
    ownerEmployeeId: IdSchema,
    statusCode: ListCodeValueSchema,
    attributionModel: NameSchema.optional(),
    results: LongTextSchema.optional(),
});
/** A partial change, including status and results. An optional field is cleared with `null`. */
export const UpdateCampaignRequestSchema = z.strictObject({
    name: NameSchema.optional(),
    objective: LongTextSchema.nullable().optional(),
    startDate: BusinessDateSchema.optional(),
    endDate: BusinessDateSchema.nullable().optional(),
    budget: BudgetInputSchema.nullable().optional(),
    targetAudienceCode: ListCodeValueSchema.nullable().optional(),
    channelCode: ListCodeValueSchema.nullable().optional(),
    ownerEmployeeId: IdSchema.optional(),
    statusCode: ListCodeValueSchema.optional(),
    attributionModel: NameSchema.nullable().optional(),
    results: LongTextSchema.nullable().optional(),
});
/** A count of records carrying one list value; `code` null counts the records with none. */
export const MarketingCountSchema = z.object({
    code: z.string().nullable(),
    count: z.number().int(),
});
/** The leads a campaign generated, counted by status and by source (FRD-MKT-028). */
export const CampaignLeadCountsSchema = z.object({
    leads: z.number().int(),
    converted: z.number().int(),
    byStatus: z.array(MarketingCountSchema),
    bySource: z.array(MarketingCountSchema),
});
// ------------------------------------------------------------------------------------- lists
/** One value of a configurable list; inactive values are included so stored codes keep a label. */
export const MarketingListValueSchema = z.object({
    valueCode: z.string(),
    label: z.string(),
    active: z.boolean(),
});
export const LeadListsSchema = z.object({
    source: z.array(MarketingListValueSchema),
    priority: z.array(MarketingListValueSchema),
    status: z.array(MarketingListValueSchema),
    qualificationStatus: z.array(MarketingListValueSchema),
    /** The CRM industry list, shared with the client master. */
    industry: z.array(MarketingListValueSchema),
});
export const CampaignListsSchema = z.object({
    status: z.array(MarketingListValueSchema),
    channel: z.array(MarketingListValueSchema),
    targetAudience: z.array(MarketingListValueSchema),
});
// ----------------------------------------------------------------------------------- summary
export const MarketingSummaryQuerySchema = z.strictObject({
    from: BusinessDateSchema,
    to: BusinessDateSchema,
});
/**
 * Operational marketing figures for a period (FRD-MKT-044, MKT-045, FRD-RPT-032), computed on the
 * server. `basis` states how each figure relates to the period. Marketing spend is not available
 * while CLR-18 is open: it is null with the reason.
 */
export const MarketingSummarySchema = z.object({
    period: z.object({
        from: z.string(),
        to: z.string(),
        basis: z.object({
            leads: z.string(),
            conversions: z.string(),
            campaigns: z.string(),
        }),
    }),
    leads: z.object({
        created: z.number().int(),
        byStatus: z.array(MarketingCountSchema),
        bySource: z.array(MarketingCountSchema),
        byCampaign: z.array(z.object({
            campaignId: IdSchema.nullable(),
            campaignCode: z.string().nullable(),
            campaignName: z.string().nullable(),
            count: z.number().int(),
        })),
    }),
    conversions: z.object({ converted: z.number().int() }),
    campaigns: z.object({
        overlapping: z.number().int(),
        byStatus: z.array(MarketingCountSchema),
        /** The planned budget of the overlapping campaigns that record one (planning information). */
        plannedBudget: AmountSchema,
        withoutBudget: z.number().int(),
    }),
    marketingSpend: z.object({ amount: AmountSchema.nullable(), reason: z.string() }),
});
//# sourceMappingURL=marketing.js.map